import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Building2, ArrowLeft, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    partitaIva: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.partitaIva) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          partitaIva: formData.partitaIva,
        }),
      });

      if (response.status === 201) {
        toast.success("Registrazione completata! Effettua il login.");
        navigate("/login");
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Errore durante la registrazione");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="register-page" 
      className="min-h-screen flex items-center justify-center p-4 bg-background"
    >
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-primary">Tyre</span>Track Pro
            </h1>
          </div>
          <CardTitle className="text-xl">Registrazione</CardTitle>
          <CardDescription>Crea un nuovo account per accedere</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Email *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="register-email"
                  placeholder="nome@esempio.it"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="partitaIva" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Partita IVA *
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="partitaIva"
                  name="partitaIva"
                  type="text"
                  data-testid="register-partita-iva"
                  placeholder="12345678901"
                  value={formData.partitaIva}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input font-mono"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Password *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="register-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Conferma Password *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  data-testid="register-confirm-password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50 border-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="w-full h-12 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Registrazione in corso...
                </>
              ) : (
                <>
                  <UserPlus size={18} className="mr-2" />
                  Registrati
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="back-to-login"
              className="text-sm text-muted-foreground hover:text-primary hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft size={14} />
              Torna al Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
